import React from 'react';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';

import Error404Page from 'components/errors/error404';
import ImpersonateHintsComponent from 'components/impersonateHints';
import NotificationsComponent from 'components/notifications';
import RestrictedRouteComponent from 'components/route/restrictedRoute';
import TwoFactorNudgeComponent from 'components/twoFactorNudge';

import BuyerPage from './buyer';
import CompanyPage from './company';
import ContactPage from './contacts';
import DebtorPage from './debtor';
import ConfirmingPage from './confirming';
import SupplierPage from './supplier';
import ISOPage from './iso';
import ProfilePage from './profile';
import SellerPage from './seller';
import SubscriptionsPage from './subscription';
import TermsAndConditionsPage from './termsAndConditions';

import { history } from 'store/index';
import {
  SIGN_IN,
  PROTECTED_APP,
  PROTECTED_APP_SUBSCRIPTION,
  PROTECTED_COMPANY_SUBSCRIPTIONS,
  PROTECTED_COMPANY_USERS,
  PROTECTED_APP_TOS,
  PROTECTED_APP_TOS_CANCEL,
  PROTECTED_APP_TOS_FAILURE,
  PROTECTED_APP_TOS_SUCCESS
} from 'constants/pageRoutes';

import {
  ROLE_BUYER,
  ROLE_CONFIRMING,
  ROLE_DEBTOR,
  ROLE_ISO,
  ROLE_SELLER,
  ROLE_SUPPLIER
} from 'constants/companyRoles';

class ProtectedApp extends React.Component {
  render() {
    // User must be identified
    if (!this.props.user || !this.props.user.activeCompany) {
      history.push(SIGN_IN);
      return 403;
    }

    const activeCompany = this.props.user.activeCompany;

    const isInitialSubscribing = !!activeCompany.isSubscribing; // new signup flow
    const mustRenew = !!activeCompany.mustRenewSubscription; // expired > 6 months flow

    const pathname = this.props.location?.pathname || '';

    const initialSubscriptionUrl = PROTECTED_APP_SUBSCRIPTION; // "/app/subscription"
    const renewalSubscriptionUrl = PROTECTED_COMPANY_SUBSCRIPTIONS; // "/app/company/subscriptions"

    const isOnInitialSubscription =
      pathname === initialSubscriptionUrl || pathname.startsWith(`${initialSubscriptionUrl}/`);

    // Allow ToS flow during initial subscription 
    const tosBase = PROTECTED_APP_TOS || '/app/terms-and-conditions';
    const isOnTermsAndConditions =
      pathname === tosBase || pathname.startsWith(`${tosBase}/`) ||
      pathname === PROTECTED_APP_TOS_CANCEL ||
      pathname === PROTECTED_APP_TOS_FAILURE ||
      pathname === PROTECTED_APP_TOS_SUCCESS;

    const isInInitialSubscriptionFlow = isOnInitialSubscription || isOnTermsAndConditions;

    const isOnRenewalSubscription =
      pathname === renewalSubscriptionUrl || pathname.startsWith(`${renewalSubscriptionUrl}/`);

    const isOnCompanyUsers =
      pathname === PROTECTED_COMPANY_USERS ||
      pathname.startsWith(`${PROTECTED_COMPANY_USERS}/`);

    const needsRenewalLegalAction =
      mustRenew && !activeCompany.hasSignedGeneralConditionsAndHasProcuration;

    const canAccessRenewalTermsAndConditions =
      needsRenewalLegalAction &&
      activeCompany.canSignGeneralConditions &&
      isOnTermsAndConditions;

    const canAccessRenewalUsers =
      needsRenewalLegalAction &&
      !activeCompany.canSignGeneralConditions &&
      isOnCompanyUsers;

    if (isInitialSubscribing && !isInInitialSubscriptionFlow) {
      return <Redirect to={initialSubscriptionUrl} />;
    }

    if (
      !isInitialSubscribing &&
      mustRenew &&
      !(
        isOnRenewalSubscription ||
        canAccessRenewalTermsAndConditions ||
        canAccessRenewalUsers
      )
    ) {
      return <Redirect to={renewalSubscriptionUrl} />;
    }

    const isLocked = isInitialSubscribing || mustRenew;
    const showTopNotifications =
      !isInitialSubscribing && (!mustRenew || needsRenewalLegalAction);

    // Conditions to be redirected / display the pages
    const isBuyer = activeCompany.role === ROLE_BUYER && !isLocked;
    const isDebtor = activeCompany.role === ROLE_DEBTOR && !isLocked;
    const isISO =
      activeCompany.role === ROLE_ISO &&
      activeCompany.roleISO.isISOPortalActive &&
      !isLocked;
    const isSeller = activeCompany.role === ROLE_SELLER && !isLocked;
    const isConfirming = activeCompany.role === ROLE_CONFIRMING && !isLocked;
    const isSupplier = activeCompany.role === ROLE_SUPPLIER && !isLocked;

    // redirect the user to the correct page (if needed)
    let redirect = <Redirect to="/app" />;

    // Default 
    if (isInitialSubscribing) {
      redirect = <Redirect to={initialSubscriptionUrl} />;
    } else if (mustRenew) {
      redirect = <Redirect to={renewalSubscriptionUrl} />;
    } else if (activeCompany.role === ROLE_ISO && !activeCompany.roleISO.isISOPortalActive) {
      redirect = <Redirect to="/app/contacts" />;
    } else if (activeCompany.role === ROLE_BUYER) {
      redirect = <Redirect to="/app/buyer" />;
    } else if (activeCompany.role === ROLE_DEBTOR) {
      redirect = <Redirect to="/app/debtor" />;
    } else if (activeCompany.role === ROLE_ISO) {
      redirect = <Redirect to="/app/iso" />;
    } else if (activeCompany.role === ROLE_SELLER) {
      redirect = <Redirect to="/app/seller" />;
    } else if (activeCompany.role === ROLE_CONFIRMING) {
      redirect = <Redirect to="/app/confirming" />;
    } else if (activeCompany.role === ROLE_SUPPLIER) {
      redirect = <Redirect to="/app/supplier" />;
    }

    return (
      <div>
        {this.props.user.isImpersonating && !isLocked && !activeCompany.canExecute && (
          <ImpersonateHintsComponent />
        )}

        {showTopNotifications && (
          <NotificationsComponent
            key={`notifications-${activeCompany.id}-${activeCompany.role}-${this.props.locale}`}
          />
        )}

        {showTopNotifications && (
          <TwoFactorNudgeComponent
            key={`twofactornudge-${activeCompany.id}-${activeCompany.role}`}
          />
        )}

        <Switch>
          <Route exact path={`${this.props.match.url}/`} render={() => redirect} />

          <Route path={`${this.props.match.url}/contacts`} component={ContactPage} />

          {/* Initial subscription flow */}
          <RestrictedRouteComponent
            path={`${this.props.match.url}/subscription`}
            condition={isInitialSubscribing}
            component={SubscriptionsPage}
            redirectUrl={PROTECTED_APP}
          />

          {/* Terms & Conditions flow (used in initial subscription/payment) */}
          <RestrictedRouteComponent
            path={`${this.props.match.url}/terms-and-conditions`}
            condition={activeCompany.canSignGeneralConditions}
            component={TermsAndConditionsPage}
            redirectUrl={PROTECTED_APP}
          />

          {/* Allow company routes (renewal subscriptions are under /company/subscriptions) */}
          <RestrictedRouteComponent
            path={`${this.props.match.url}/company`}
            condition={true}
            component={CompanyPage}
            redirectUrl={PROTECTED_APP}
          />

          <RestrictedRouteComponent
            path={`${this.props.match.url}/buyer`}
            condition={isBuyer}
            component={BuyerPage}
            redirectUrl={PROTECTED_APP}
          />
          <RestrictedRouteComponent
            path={`${this.props.match.url}/debtor`}
            condition={isDebtor}
            component={DebtorPage}
            redirectUrl={PROTECTED_APP}
          />
          <RestrictedRouteComponent
            path={`${this.props.match.url}/iso`}
            condition={isISO}
            component={ISOPage}
            redirectUrl={PROTECTED_APP}
          />
          <RestrictedRouteComponent
            path={`${this.props.match.url}/seller`}
            condition={isSeller}
            component={SellerPage}
            redirectUrl={PROTECTED_APP}
          />
          <RestrictedRouteComponent
            path={`${this.props.match.url}/confirming`}
            condition={isConfirming}
            component={ConfirmingPage}
            redirectUrl={PROTECTED_APP}
          />
          <RestrictedRouteComponent
            path={`${this.props.match.url}/supplier`}
            condition={isSupplier}
            component={SupplierPage}
            redirectUrl={PROTECTED_APP}
          />

          <RestrictedRouteComponent
            path={`${this.props.match.url}/profile`}
            condition={!isLocked}
            component={ProfilePage}
            redirectUrl={PROTECTED_APP}
          />

          <Route component={Error404Page} />
        </Switch>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  locale: state.locale.locale,
  user: state.auth.user
});

export default withRouter(connect(mapStateToProps)(ProtectedApp));
